import React, { useState, useEffect } from 'react';
import './Etape2.css';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import {
  loadEtape2ReferentielSelection,
  normeDisplayTitle,
  normeDisplayDescription,
  arreteDisplay,
  isLegacyArreteKey,
} from '../services/etape2ReferentielService';

const DEFAULT_DECRETS = {
  decret_2010_1016: true,
  decret_2010_1017: true,
};

function Etape2({ mission, onBack, onSave }) {
  const [normeRows, setNormeRows] = useState([]);
  const [arreteRows, setArreteRows] = useState([]);
  const [referentielOk, setReferentielOk] = useState(false);
  const [ready, setReady] = useState(false);

  const [formData, setFormData] = useState({
    decrets: { ...DEFAULT_DECRETS },
    normes: {},
    arretes: {},
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const sel = await loadEtape2ReferentielSelection();
      const saved = await inspectionStorage.getStep(mission.id, 'etape_2_normes');
      if (cancelled) return;

      const baseNormes = Object.fromEntries(sel.normes.map((r) => [r.etape2_key, false]));
      const baseArretes = Object.fromEntries(sel.arretes.map((r) => [r.etape2_key, false]));

      setNormeRows(sel.normes);
      setArreteRows(sel.arretes);
      setReferentielOk(sel.fromApi);
      setFormData({
        decrets: { ...DEFAULT_DECRETS, ...(saved?.decrets || {}) },
        normes: { ...baseNormes, ...(saved?.normes || {}) },
        arretes: { ...baseArretes, ...(saved?.arretes || {}) },
      });
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [mission.id]);

  const updateCheckbox = (category, field, checked) => {
    setFormData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: checked,
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const stepData = {
        ...formData,
        timestamp: new Date().toISOString(),
      };

      await inspectionStorage.saveStep(mission.id, 'etape_2_normes', stepData);

      syncManager.syncStep(mission.id, 'etape_2_normes', stepData);

      if (onSave) {
        onSave(formData);
      }

      alert('✅ Étape 2 sauvegardée avec succès !');

      onBack();
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className="etape-container">
        <div className="etape-header">
          <button type="button" className="back-button" onClick={onBack}>
            ← Retour
          </button>
          <div className="etape-title">
            <h1>Étape 2/14</h1>
            <p>Chargement…</p>
          </div>
          <div></div>
        </div>
        <div className="etape-content">
          <div className="alert alert-info">Chargement des normes et arrêtés…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="etape-container">
      <div className="etape-header">
        <button type="button" className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <div className="etape-title">
          <h1>Étape 2/14</h1>
          <p>Normes et réglementations</p>
        </div>
        <div></div>
      </div>

      <div className="etape-content">
        <div className="alert alert-info">
          ℹ️ Sélectionnez les normes et réglementations applicables à cette vérification. Ces textes seront référencés dans le rapport.
          {referentielOk && (
            <span className="block" style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              Liste synchronisée avec le référentiel actif (API).
            </span>
          )}
        </div>

        <div className="form-section">
          <h2>📜 Décrets</h2>

          <div className="checkbox-list">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={formData.decrets.decret_2010_1016}
                onChange={(e) => updateCheckbox('decrets', 'decret_2010_1016', e.target.checked)}
              />
              <div className="checkbox-content">
                <strong>Décret 2010-1016</strong>
                <p>Obligations de l&apos;employeur pour l&apos;utilisation des installations électriques</p>
              </div>
            </label>

            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={formData.decrets.decret_2010_1017}
                onChange={(e) => updateCheckbox('decrets', 'decret_2010_1017', e.target.checked)}
              />
              <div className="checkbox-content">
                <strong>Décret 2010-1017</strong>
                <p>Obligations des maîtres d&apos;ouvrage lors de la conception des installations électriques</p>
              </div>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>📋 Normes</h2>

          <div className="checkbox-list">
            {normeRows.map((row) => {
              const key = row.etape2_key;
              const title = normeDisplayTitle(row);
              const description = normeDisplayDescription(row);
              const isDynamic = key.startsWith('ref_norme_');
              return (
                <label key={key} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.normes[key] || false}
                    onChange={(e) => updateCheckbox('normes', key, e.target.checked)}
                  />
                  <div className="checkbox-content">
                    <strong>
                      {title}
                      {isDynamic && (
                        <span style={{ marginLeft: '0.35rem', fontSize: '0.75rem', color: '#5b21b6' }}>
                          (référentiel)
                        </span>
                      )}
                    </strong>
                    {description ? <p>{description}</p> : null}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="form-section">
          <h2>📄 Arrêtés</h2>

          <div className="checkbox-list">
            {arreteRows.map((row) => {
              const key = row.etape2_key;
              const { title, description } = arreteDisplay(row);
              const isDynamic = !isLegacyArreteKey(key);
              return (
                <label key={key} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.arretes[key] || false}
                    onChange={(e) => updateCheckbox('arretes', key, e.target.checked)}
                  />
                  <div className="checkbox-content">
                    <strong>
                      {title}
                      {isDynamic && (
                        <span style={{ marginLeft: '0.35rem', fontSize: '0.75rem', color: '#c2410c' }}>
                          (référentiel)
                        </span>
                      )}
                    </strong>
                    {description ? <p>{description}</p> : null}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <div className="etape-actions">
        <button type="button" className="btn btn-secondary" onClick={onBack} disabled={isSaving}>
          Annuler
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder et continuer'}
        </button>
      </div>
    </div>
  );
}

export default Etape2;
